import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createManualGroup,
  exportGroupBindings,
  fetchGroupBindings,
  fetchZentaoMappingOptions,
  generateZentaoProjectForBinding,
  recommendGroupBindingProduct,
  suggestGroupBindingProjectName,
  syncZentaoMappingOptions,
  updateGroupBinding,
} from '@/services/api/groupBindings'
import type { GroupBindingListParams, GroupBindingUpdatePayload, ManualGroupCreatePayload } from '@/types/groupBinding'

export function useCreateManualGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ManualGroupCreatePayload) => createManualGroup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-bindings'] })
    },
  })
}

export function useGroupBindingList(params: GroupBindingListParams) {
  return useQuery({
    queryKey: ['group-bindings', 'list', params],
    queryFn: () => fetchGroupBindings(params),
    staleTime: 15_000,
  })
}

export function useExportGroupBindings() {
  return useMutation({
    mutationFn: exportGroupBindings,
  })
}

export function useUpdateGroupBinding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ bindingId, payload }: { bindingId: number; payload: GroupBindingUpdatePayload }) =>
      updateGroupBinding(bindingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-bindings'] })
      queryClient.invalidateQueries({ queryKey: ['drafts'] })
    },
  })
}

export function useZentaoMappingOptions(projectId?: number, enabled = true) {
  return useQuery({
    queryKey: ['group-bindings', 'zentao-options', projectId],
    queryFn: () => fetchZentaoMappingOptions(projectId),
    enabled,
    staleTime: 60_000,
  })
}

export function useSyncZentaoMappingOptions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: syncZentaoMappingOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-bindings', 'zentao-options'] })
      queryClient.invalidateQueries({ queryKey: ['group-bindings', 'list'] })
    },
  })
}

export function useSuggestGroupBindingProjectName() {
  return useMutation({
    mutationFn: suggestGroupBindingProjectName,
  })
}

export function useRecommendGroupBindingProduct() {
  return useMutation({
    mutationFn: recommendGroupBindingProduct,
  })
}

export function useGenerateZentaoProjectForBinding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: generateZentaoProjectForBinding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-bindings'] })
      queryClient.invalidateQueries({ queryKey: ['drafts'] })
    },
  })
}
